import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../hooks/useAuth';
import { CheckCircle, Crown, AlertCircle } from 'lucide-react';

const REDIRECT_DELAY_MS = 10000;

export default function FreeTierSuccess() {
    const { isDarkMode } = useTheme();
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [membership, setMembership] = useState(null);
    // An organization license that comes with a plan covers it (same check as
    // signup's "Your plan is covered" step); null when there's none
    const [coveredBy, setCoveredBy] = useState(null);

    useEffect(() => {
        // Wait until auth has settled and a user is confirmed before hitting
        // /membership/me — same guard as MyMembership/MembershipPlans, to avoid
        // firing during the post-login auth flap.
        if (authLoading) return;
        if (!user) {
            // Auth settled with no confirmed user (e.g. session expired right as
            // this page loaded) — don't leave the spinner running forever.
            navigate('/login', { replace: true });
            return;
        }

        let cancelled = false;

        const fetchMembership = async () => {
            try {
                //old const response = await api.get('/membership/me');
                // The org check is best effort: without it the page shows the free plan as before
                const [response, context] = await Promise.all([
                    api.get('/membership/me'),
                    api.get('/org/context').catch(() => null),
                ]);
                if (cancelled) return;
                // Only an answer changes it: a failed re-fetch (after an auth refresh) keeps what's shown
                if (context) {
                    const orgs = context.data?.role === 'student' && Array.isArray(context.data.organizations) ? context.data.organizations : [];
                    setCoveredBy(orgs.find((o) => o.HasLicense && o.SponsoredTierName) || null);
                }
                setMembership(response.data);
                setStatus('success');
            } catch (err) {
                if (!cancelled) setStatus('error');
            }
        };

        fetchMembership();

        return () => {
            cancelled = true;
        };
    }, [authLoading, user, navigate]);

    // Redirect to the dashboard after a delay long enough to read the confirmation.
    // Kept separate from the fetch above so an auth refresh (new `user` object)
    // re-fetching the membership doesn't restart the countdown.
    useEffect(() => {
        if (status !== 'success') return;
        const redirectTimer = setTimeout(() => navigate('/userdashboard'), REDIRECT_DELAY_MS);
        return () => clearTimeout(redirectTimer);
    }, [status, navigate]);

    return (
        <div className={`flex items-center justify-center min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`}>
            <div className={`max-w-md w-full mx-4 rounded-3xl border shadow-lg p-10 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
                {status === 'verifying' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <div className="animate-spin rounded-full h-14 w-14 border-t-2 border-b-2 border-primary-500"></div>
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Setting up your membership...
                        </h2>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <CheckCircle className="text-primary-500" size={64} />
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Subscription Successful!
                        </h2>
                        <div className={`mt-4 mb-6 rounded-2xl p-4 ${isDarkMode ? 'bg-slate-700' : 'bg-primary-50'}`}>
                            <p className={`text-sm font-bold uppercase tracking-widest mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                Your Plan
                            </p>
                            <p className={`text-xl font-bold flex items-center justify-center gap-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                                {/* old {membership?.TierName || 'Free'} */}
                                {coveredBy ? coveredBy.SponsoredTierName : (membership?.TierName || 'Free')}
                                <Crown className="text-amber-400" size={20} />
                            </p>
                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                {/* old $0.00 */}
                                {coveredBy ? `Covered by ${coveredBy.OrgName}` : '$0.00'}
                            </p>
                        </div>
                        <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Redirecting you to your dashboard...
                        </p>
                        <button
                            className="mt-4 px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-semibold transition-all"
                            onClick={() => navigate('/userdashboard')}
                        >
                            Go to Dashboard
                        </button>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="flex justify-center mb-6">
                            <AlertCircle className="text-red-500" size={64} />
                        </div>
                        <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                            Something went wrong
                        </h2>
                        <p className={`text-sm mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            We could not confirm your membership. Please check your membership page.
                        </p>
                        <button
                            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-all"
                            onClick={() => navigate('/membership')}
                        >
                            Go to My Membership
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
